'use client'

import { UserData, postSignUp } from 'data/authentication'
import { ComponentType, useState } from 'react'
import Link from 'components/router/Link'
import Field from 'components/form/Field'
import Form from 'components/form/Form'
import { privacyPolicyUrl, termsOfUsePath } from 'shared/urlHelpers'
import { ResendVerificationEmail } from 'components/auth/ResendVerificationEmail'
import { FormSubmissionPanel } from 'components/form/FormSubmissionPanel'
import {
  formValidation,
  formSubmitValidation
} from 'components/form/formValidation'
import { Heading } from 'components/ui/text/Heading'

interface SignupFormProps extends UserData {
  passwordRepeat: string
  registerReferrer: string
  dataPrivacy: boolean
}

const validate = (d: SignupFormProps) =>
  formValidation<SignupFormProps>(d, {
    firstname: ['required'],
    lastname: ['required'],
    username: ['required'],
    email: ['required', 'email'],
    password: ['required', 'password'],
    passwordRepeat: ['required', 'password', 'passwordRepeat'],
    dataPrivacy: ['required']
  })

const SignupForm: ComponentType = () => {
  const [isSuccess, setIsSuccess] = useState<boolean>(false)
  const [resendEmailToken, setResendEmailToken] = useState<
    string | null | undefined
  >(null)

  const formSubmit = async (data: SignupFormProps) => {
    const resp = await postSignUp({
      ...data,
      firstname: data.firstname.trim(),
      lastname: data.lastname.trim(),
      username: data.username.trim(),
      email: data.email.trim()
    })

    if (resp.valid) {
      setIsSuccess(true)
      setResendEmailToken(resp.jwt)
    } else {
      setIsSuccess(false)
      return formSubmitValidation(resp.errors)
    }
  }

  if (isSuccess) {
    return (
      <div className="mb-4 mt-4">
        <Heading level={2}>Link versandt</Heading>
        <p className="mb-4 prose">
          In Kürze erhältst Du einen Link per E-Mail, um Dein Benutzerkonto zu
          aktivieren.
        </p>
        {resendEmailToken && (
          <ResendVerificationEmail token={resendEmailToken} />
        )}
      </div>
    )
  }

  return (
    <div className="mb-4 mt-4">
      <Form className="w-full form" onSubmit={formSubmit} validate={validate}>
        <Field
          name="salutation"
          label="Anrede"
          className="mb-4"
          input={(p) => (
            <select {...p}>
              <option></option>
              <option value="M">Herr</option>
              <option value="F">Frau</option>
              <option value="D">Divers</option>
            </select>
          )}
        />
        <Field
          name="firstname"
          label="Vorname"
          className="mb-4"
          required
          input={(p) => <input {...p} type="text" />}
        />

        <Field
          name="lastname"
          label="Nachname"
          className="mb-4"
          required
          input={(p) => <input {...p} type="text" />}
        />
        <Field
          name="username"
          label="Nutzername"
          className="mb-4"
          required
          input={(p) => <input {...p} type="text" />}
        />

        <Field
          name="email"
          label="Email"
          className="mb-4"
          required
          input={(p) => <input {...p} type="email" autoComplete="username" />}
        />

        <Field
          name="password"
          label="Passwort"
          className="mb-4"
          required
          input={(p) => (
            <input {...p} type="password" autoComplete="new-password" />
          )}
        />

        <Field
          name="passwordRepeat"
          label="Passwort wiederholen"
          className="mb-4"
          required
          input={(p) => (
            <input {...p} type="password" autoComplete="repeat-password" />
          )}
        />

        <Field
          name="registerReferrer"
          label={
            <>
              Wie bist du auf{' '}
              <a href="https://www.handball.net/">Handball.net</a> aufmerksam
              geworden?
            </>
          }
          className="mb-4"
          input={(p) => (
            <select {...p}>
              <option></option>
              <option>
                Handball.net-Kanal (Social Media, Newsletter, Webseite, etc.)
              </option>
              <option>
                DHB-Kanal (Social Media, Newsletter, Webseite, etc.)
              </option>
              <option>Landesverband</option>
              <option>Verein</option>
              <option>Medien</option>
              <option>Gewinnspiel</option>
              <option>Freunde / Bekannte</option>
              <option>Sonstiges</option>
            </select>
          )}
        />

        <Field
          name="dataPrivacy"
          label={
            <span>
              Ich habe die{' '}
              <Link href={privacyPolicyUrl()} openInNewWindow>
                Datenschutzbestimmungen
              </Link>
              und die <Link href={termsOfUsePath()}>Nutzungsbedingungen</Link>{' '}
              von handball.net zur Kenntnis genommen.
            </span>
          }
          className="mb-4"
          labelPosition="right"
          required
          type="checkbox"
          input={(p) => <input {...p} type="checkbox" />}
        />

        <Field
          name="newsletter"
          label={
            <span>
              Ich möchte zukünftig per handball.net-Newsletter über spannende
              News und Angebote des Deutschen Handballbunds e.V. informiert
              werden.. Der Versand des Newsletters erfolgt freiwillig auf Basis
              deiner Einwilligung. Du kannst deine Einwilligung jederzeit
              widerrufen. Die Rechtmäßigkeit der bis zum Widerruf der
              Einwilligung erfolgten Vereinbarung wird davon nicht berührt.
              Weitere Informationen findest du in der{' '}
              <Link href={privacyPolicyUrl()} openInNewWindow>
                Datenschutzerklärung
              </Link>
              .
            </span>
          }
          className="mb-4"
          labelPosition="right"
          type="checkbox"
          input={(p) => <input {...p} type="checkbox" />}
        />

        <FormSubmissionPanel buttonText="Registrieren" />
      </Form>
    </div>
  )
}

export default SignupForm
